import { apiFetch } from "./client";
import i18n from "../i18n";

export type CompanyStatus = "ACTIVE" | "SUSPENDED" | "DELETED";
export type CompanyRelation = "PROSPECT" | "CLIENT";
export type CoverageMode = "INHERIT" | "DIRECT";
export type UserStatus = "ACTIVE" | "LOCKED" | "DISABLED";

export type PriceList = { id: string; code: string; name: string; currency: string; isDefault: boolean };

export type Company = {
  id: string;
  name: string;
  vatNumber: string | null;
  erpId: string | null;
  country: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  postalCode: string | null;
  defaultLocale: string;
  status: CompanyStatus;
  serviceAccountId: string | null;
  serviceAccountName: string | null;
  priceListId?: string | null;
  priceListName?: string | null;
  displayDiscount: boolean;
  forecastsEnabled: boolean;
  moq: number | null;
  standardRebatePct: number | null;
  maxRebatePct: number | null;
  createdAt: string;
  updatedAt: string;
  relation: CompanyRelation;
  convertedFromLeadId: string | null;
  becameClientAt: string | null;
  impGroup: boolean;
  coverageMode: CoverageMode;
  /** Primary sales rep's name. Sent by the list only; absent elsewhere. */
  salesRepName?: string | null;
  /** Payment terms the customer carries by default; an order or offer may override them. */
  paymentMethod?: string | null;
  paymentDays?: number | null;
  /** PANTHEON's own classification, imported. The VAT determinant — read-only here. */
  erpWayOfSale?: string | null;
};

export type CompanyCreateBody = {
  name: string;
  vatNumber?: string;
  erpId?: string;
  country?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  postalCode?: string;
  defaultLocale?: string;
  status?: CompanyStatus;
  displayDiscount?: boolean;
  forecastsEnabled?: boolean;
  moq?: number;
  standardRebatePct?: number;
  maxRebatePct?: number;
  assignedSalesUserId?: string;
  serviceAccountId?: string;
  priceListId?: string;
  impGroup?: boolean;
  coverageMode?: CoverageMode;
  /** Required on create — PANTHEON expects a customer to carry at least one contact person. */
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  /**
   * The customer's standing payment terms. Unlike `moq` and the rebates, the server has no
   * "unchanged" convention for these — update() writes whatever arrives, so every caller that
   * PUTs a company must send the pair it means to keep, or it is cleared.
   */
  paymentMethod?: string | null;
  paymentDays?: number | null;
};

export type User = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  defaultLocale: string;
  /**
   * ERP clerk codes this person holds. Several, because the ERP register can carry the same person
   * more than once (Matjaž Hribar is both 20 and 36) and each code brings its own companies.
   * Admin-only to change — it decides which companies the next import assigns them.
   */
  erpIds: string[];
  phone: string | null;
  avatarUrl: string | null;
  status: UserStatus;
  companyId: string | null;
  companyName: string | null;
  impInternal: boolean;
  tmAdmin: boolean;
  maxDiscountPct: number | null;
  roles: string[];
  lastLoginAt: string | null;
  createdAt: string;
  /** Soft-deleted. Pickers must drop these; the admin list may still choose to show them. */
  deleted: boolean;
};

/** One entry in a company's address book (IMP-13 delivery / IMP-14 billing). */
export type CompanyAddress = {
  id: string;
  companyId: string;
  label: string | null;
  recipientName: string | null;
  addressLine1: string;
  addressLine2: string | null;
  city: string | null;
  postalCode: string | null;
  country: string | null;
  vatNumber: string | null;
  usableForDelivery: boolean;
  usableForBilling: boolean;
  defaultDelivery: boolean;
  defaultBilling: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
};

/** Create/update body for an address-book entry. The `default*` flags are only
 *  honoured on the internal management path; the customer inline-create derives
 *  defaults server-side. */
export type CompanyAddressBody = {
  label?: string | null;
  recipientName?: string | null;
  addressLine1: string;
  addressLine2?: string | null;
  city?: string | null;
  postalCode?: string | null;
  country?: string | null;
  vatNumber?: string | null;
  usableForDelivery?: boolean;
  usableForBilling?: boolean;
  defaultDelivery?: boolean;
  defaultBilling?: boolean;
  active?: boolean;
};

export type AddressPurpose = "delivery" | "billing";

/** A named person at a company who is NOT a portal login user (that is a User). */
export type CompanyContact = {
  id: string;
  companyId: string;
  name: string;
  title: string | null;
  department: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  notes: string | null;
  primary: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
};

export type CompanyContactBody = {
  name: string;
  title?: string | null;
  department?: string | null;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  notes?: string | null;
  primary?: boolean;
  active?: boolean;
};

export type SalesAssignment = {
  id: string;
  companyId: string;
  salesUserId: string;
  salesUserEmail: string;
  salesUserFullName: string;
  primary: boolean;
  assignedAt: string;
};

export type SalesCoverageEntry = {
  id: string | null;           // null for SCOPE entries
  userId: string;
  name: string;
  email: string;
  primary: boolean;
  assignedAt: string | null;
  origin: "DIRECT" | "SCOPE";
  countryCode: string | null;  // populated for SCOPE entries
};

export type SalesCoverage = {
  direct: SalesCoverageEntry[];
  viaScope: SalesCoverageEntry[];
};

export type Invitation = {
  id: string;
  email: string;
  companyId: string | null;
  companyName: string | null;
  roles: string[];
  invitedBy: string | null;
  expiresAt: string;
  acceptedAt: string | null;
  createdAt: string;
  /** The link no longer works — decided by the server. Resend issues a new one. */
  expired: boolean;
};

export type Page<T> = {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
};

const buildPageQuery = (page: number, size: number) =>
  `?page=${page}&size=${size}&sort=createdAt,desc`;

export const adminApi = {
  // Companies
  listCompanies: (
    page = 0,
    size = 20,
    relation?: CompanyRelation | "ALL",
    q?: string,
    country?: string,
    status?: string,
    /** A sales user's id, or "none" for companies with no rep at all. */
    salesUser?: string,
  ) =>
    apiFetch<Page<Company>>(
      `/admin/companies${buildPageQuery(page, size)}` +
        (relation ? `&relation=${relation}` : "") +
        (q ? `&q=${encodeURIComponent(q)}` : "") +
        (country ? `&country=${encodeURIComponent(country)}` : "") +
        (status ? `&status=${encodeURIComponent(status)}` : "") +
        (salesUser ? `&salesUser=${encodeURIComponent(salesUser)}` : ""),
    ),
  markClient: (companyId: string, body: { priceListId?: string | null; sendInvitation?: boolean }) =>
    apiFetch<MarkClientResult>(`/companies/${companyId}/mark-client`, { method: "POST", body }),
  getCompany: (id: string) => apiFetch<Company>(`/admin/companies/${id}`),
  createCompany: (body: CompanyCreateBody) =>
    apiFetch<Company>("/admin/companies", { method: "POST", body }),
  updateCompany: (id: string, body: Partial<Company>) =>
    apiFetch<Company>(`/admin/companies/${id}`, { method: "PUT", body }),
  deleteCompany: (id: string) =>
    apiFetch<void>(`/admin/companies/${id}`, { method: "DELETE" }),
  listCompanySales: (id: string) =>
    apiFetch<SalesAssignment[]>(`/admin/companies/${id}/sales-assignments`),
  listCompanySalesCoverage: (id: string) =>
    apiFetch<SalesCoverage>(`/admin/companies/${id}/sales-coverage`),
  /** Count for the admin dashboard card; the list behind it is the same filter, salesUser=none. */
  unassignedCompanyCount: () =>
    apiFetch<{ count: number }>("/admin/companies/unassigned-count"),

  assignSales: (id: string, body: { salesUserId: string; primary: boolean }) =>
    apiFetch<SalesAssignment>(`/admin/companies/${id}/sales-assignments`, {
      method: "POST",
      body,
    }),
  removeSales: (companyId: string, assignmentId: string) =>
    apiFetch<void>(
      `/admin/companies/${companyId}/sales-assignments/${assignmentId}`,
      { method: "DELETE" },
    ),
  listPriceLists: () => apiFetch<PriceList[]>("/admin/price-lists"),

  // Company address book (internal management, companies:edit)
  listCompanyAddresses: (companyId: string) =>
    apiFetch<CompanyAddress[]>(`/admin/companies/${companyId}/addresses`),
  createCompanyAddress: (companyId: string, body: CompanyAddressBody) =>
    apiFetch<CompanyAddress>(`/admin/companies/${companyId}/addresses`, {
      method: "POST",
      body,
    }),
  updateCompanyAddress: (companyId: string, addressId: string, body: CompanyAddressBody) =>
    apiFetch<CompanyAddress>(`/admin/companies/${companyId}/addresses/${addressId}`, {
      method: "PUT",
      body,
    }),
  deleteCompanyAddress: (companyId: string, addressId: string) =>
    apiFetch<void>(`/admin/companies/${companyId}/addresses/${addressId}`, {
      method: "DELETE",
    }),
  setCompanyAddressDefault: (companyId: string, addressId: string, purpose: AddressPurpose) =>
    apiFetch<CompanyAddress>(
      `/admin/companies/${companyId}/addresses/${addressId}/set-default?purpose=${purpose}`,
      { method: "PATCH" },
    ),

  // Company contact book (internal management, companies:edit)
  listCompanyContacts: (companyId: string) =>
    apiFetch<CompanyContact[]>(`/admin/companies/${companyId}/contacts`),
  createCompanyContact: (companyId: string, body: CompanyContactBody) =>
    apiFetch<CompanyContact>(`/admin/companies/${companyId}/contacts`, { method: "POST", body }),
  updateCompanyContact: (companyId: string, contactId: string, body: CompanyContactBody) =>
    apiFetch<CompanyContact>(`/admin/companies/${companyId}/contacts/${contactId}`, {
      method: "PUT",
      body,
    }),
  deleteCompanyContact: (companyId: string, contactId: string) =>
    apiFetch<void>(`/admin/companies/${companyId}/contacts/${contactId}`, { method: "DELETE" }),

  // Users
  listUsers: (page = 0, size = 20) =>
    apiFetch<Page<User>>(`/admin/users${buildPageQuery(page, size)}`),
  getUser: (id: string) => apiFetch<User>(`/admin/users/${id}`),
  updateUser: (id: string, body: Partial<User>) =>
    apiFetch<User>(`/admin/users/${id}`, { method: "PUT", body }),
  enableUser: (id: string) =>
    apiFetch<User>(`/admin/users/${id}/enable`, { method: "POST" }),
  disableUser: (id: string) =>
    apiFetch<User>(`/admin/users/${id}/disable`, { method: "POST" }),
  deleteUser: (id: string) =>
    apiFetch<void>(`/admin/users/${id}`, { method: "DELETE" }),
  resetUserTwoFactor: (id: string) =>
    apiFetch<void>(`/admin/users/${id}/reset-2fa`, { method: "POST" }),

  // Invitations
  /** Every invitation not yet accepted — expired ones included, flagged `expired`. */
  listOpenInvitations: () => apiFetch<Invitation[]>("/admin/invitations"),
  createInvitation: (body: {
    email: string;
    companyId?: string;
    roles: string[];
    locale?: string;
    erpId?: string;
    tmAdmin?: boolean;
    maxDiscountPct?: number;
  }) => apiFetch<Invitation>("/admin/invitations", { method: "POST", body }),
  resendInvitation: (id: string) =>
    apiFetch<Invitation>(`/admin/invitations/${id}/resend`, { method: "POST" }),
  revokeInvitation: (id: string) =>
    apiFetch<void>(`/admin/invitations/${id}`, { method: "DELETE" }),
};

export type MarkClientResult = {
  companyId: string;
  relation: CompanyRelation;
  becameClientAt: string | null;
  erpOutboxId: string | null;
  invitationSent: boolean;
  invitationSkippedReason: "NOT_REQUESTED" | "NO_CONTACT" | "ALREADY_INVITED" | null;
};

/** company.error.<CODE> when a translation exists; otherwise the raw server message. */
export function companyErrorMessage(e: unknown): string {
  const err = e as { code?: string; message?: string };
  if (!err || typeof err.code !== "string") return String(err?.message ?? e);
  const key = `company.error.${err.code}`;
  return i18n.exists(key) ? i18n.t(key) : (err.message ?? key);
}
